import { useState, useMemo, useCallback, useRef } from 'react';
import { ShieldOff, ClipboardList } from 'lucide-react';
import { Card } from '@components/base/layout';
import { actionAnchorId } from '../configs/otherActionsConfig';
import { Text, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { useToast } from '@components/base/feedback';
import { EntityForm, useEntityForm } from '@components/integrated/entity-form';
import type {
  EntityFormConfig,
  CheckboxGroupFieldOptions,
  UseEntityFormReturn,
} from '@components/integrated/entity-form';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromEntityForm,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { claimflowApiClient } from '../api/claimflowApiClient';
import type { VeosGuaranteeToClose } from '../api/taskService';
import { WorkflowStepTracker } from './WorkflowStepTracker';
import type { WorkflowStepExecutionSummary } from './WorkflowStepTracker';

interface CloseCoverageCardProps {
  ses: string;
  idSin: string;
  guarantees: VeosGuaranteeToClose[];
  /**
   * Per-tenant field layout overrides injected by the `withComponentRules`
   * HOC. Underscored to match the injected meta-prop convention; `undefined`
   * at baseline ⇒ the form config is returned unchanged (byte-identical
   * render).
   */
  _layoutOverrides?: LayoutOverrides;
  /** Fired after a successful close so the host can refetch the task. */
  onCoverageClosed?: () => void;
}

export function CloseCoverageCard({
  ses,
  idSin,
  guarantees,
  _layoutOverrides,
  onCoverageClosed,
}: Readonly<CloseCoverageCardProps>) {
  const { toast } = useToast();

  // Collapsed on first paint — every card in the claim view's right column
  // opens folded.
  const [expanded, setExpanded] = useState(false);
  const [failedSteps, setFailedSteps] = useState<WorkflowStepExecutionSummary[]>([]);

  const formRef = useRef<UseEntityFormReturn | null>(null);

  const mutationFn = useCallback(
    async (payload: Record<string, unknown> | FormData) => {
      const codes = payload instanceof FormData
        ? JSON.parse(payload.get('codes_garanties') as string)
        : payload.codes_garanties;
      const response = await claimflowApiClient.post(
        '/claimflow/workflows/claimflow-close-coverage/execute',
        {
          context_data: {
            ses,
            id_sin: idSin,
            codes_garanties: codes,
          },
        },
      );
      return response.data;
    },
    [ses, idSin],
  );

  const baseConfig: EntityFormConfig = useMemo(
    () => ({
      id: 'close-coverage',
      endpoint: {
        erpEndpoint:
          '/api/claimflow/workflows/claimflow-close-coverage/execute',
        method: 'POST',
      },
      fields: {
        codes_garanties: {
          type: 'checkboxGroup',
          label: '',
          validation: {
            required: 'Veuillez sélectionner au moins une garantie',
          },
          options: {
            items: guarantees.map((g) => ({
              value: g.CD_GAR,
              label: g.LIB_GAR,
            })),
            orientation: 'horizontal',
          } as CheckboxGroupFieldOptions,
        },
      },
      actions: {
        submit: { visible: true, label: 'Clôturer les garanties' },
        cancel: { visible: false },
        reset: { visible: false },
      },
      layout: { columns: 1 },
    }),
    [guarantees],
  );

  usePublishLayoutConfig(
    'claimflow.closeCoverageCard',
    buildPublishedConfigFromEntityForm(baseConfig, 'Clôture de garanties'),
  );

  const formConfig: EntityFormConfig = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );

  const handleSuccess = useCallback(() => {
    setFailedSteps([]);
    formRef.current?.reset({ codes_garanties: [] });
    toast({ variant: 'success', title: 'Garanties clôturées' });
    onCoverageClosed?.();
    setExpanded(false);
  }, [toast, onCoverageClosed]);

  const handleError = useCallback((error: unknown) => {
    const stepExecutions = extractStepExecutions(error);
    setFailedSteps(stepExecutions);
  }, []);

  const form = useEntityForm({
    config: formConfig,
    onSuccess: handleSuccess,
    onError: handleError,
    mutationFn,
  });

  formRef.current = form;

  return (
    <Card id={actionAnchorId('claimflow.closeCoverageCard')}>
      <Card.Header
        collapsible
        defaultExpanded={expanded}
        onExpandChange={setExpanded}
      >
        <Icon icon={ShieldOff} size="md" color="primary" />
        <Text as="span" weight="semibold" size="base">
          {resolveCardTitle('Clôture de garanties', _layoutOverrides)}
        </Text>
      </Card.Header>
      <Card.Content>
        <Text size="sm" color="muted" className="mb-4">
          Sélectionnez les garanties ouvertes que vous souhaitez clôturer :
        </Text>
        <EntityForm config={formConfig} form={form}>
          <EntityForm.Alert />
          {failedSteps.length > 0 && (
            <div className="mb-4">
              <Text size="sm" weight="semibold" className="mb-2">
                Détail de l&apos;exécution :
              </Text>
              <WorkflowStepTracker steps={failedSteps} />
            </div>
          )}
          <EntityForm.Fields />
          {formConfig.actions?.submit?.visible !== false && (
            <div className="pt-4">
              <Button
                type="submit"
                variant="primary"
                leftIcon={<Icon icon={ClipboardList} size="sm" />}
                loading={form.isSubmitting}
                disabled={form.isSubmitting}
                className="w-full"
              >
                {formConfig.actions?.submit?.label ?? 'Clôturer les garanties'}
              </Button>
            </div>
          )}
        </EntityForm>
      </Card.Content>
    </Card>
  );
}

function extractStepExecutions(error: unknown): WorkflowStepExecutionSummary[] {
  const data = getErrorData(error);
  if (!data) return [];

  const executions = (data as Record<string, unknown>).step_executions;
  if (!Array.isArray(executions)) return [];

  return executions.map((exec: Record<string, unknown>) => ({
    name: (exec.step as Record<string, unknown>)?.name as string ?? `Étape ${String(exec.workflow_step_id).slice(0, 8)}`,
    status: exec.status as WorkflowStepExecutionSummary['status'],
    errorMessage: exec.error_message as string | null ?? null,
  }));
}

function getErrorData(error: unknown): unknown {
  if (error === null || typeof error !== 'object') return null;
  const err = error as Record<string, unknown>;

  const response = err.response;
  if (response !== null && typeof response === 'object') {
    const respObj = response as Record<string, unknown>;
    const respData = respObj.data;
    if (respData !== null && typeof respData === 'object') {
      const nested = (respData as Record<string, unknown>).data;
      if (nested !== null && typeof nested === 'object') return nested;
      return respData;
    }
  }

  const data = err.data;
  if (data !== null && typeof data === 'object') return data;

  return null;
}
